import { get } from 'lodash';
import type { EngineResult } from 'veritone-types';
import {
  BoundingPolyRect,
  RedactionConfig,
  ShapeType,
} from '@common-modules/mediaDetails/models';
import { DeepNonNullable, DeepPick, DeepReadonly } from '@utils';
import { Adapter } from '../interfaces';
import { DetectionEngineNormalizedData } from './models';
import { IndividualRedactionCode } from '@redact/state/modules/redactionCodes/models';
import { arrayHasContents } from '@common/shared/util';
import { DETECTION_OBJECT_TYPE } from '@helpers/constants';

type HeadDetectionEngineResultV1 = Omit<EngineResult, 'jsondata'> & {
  tdoId: string;
  assetId: string;
  engineId: string;
  startOffsetMs: number;
  stopOffsetMs: number;
  jsondata: {
    generatedDateUTC: string;
    modifiedDateTime: number;
    sourceEngineId: string;
    taskId: string;
    series: {
      startTimeMs: number;
      stopTimeMs: number;
      object: {
        label: string;
        type: DETECTION_OBJECT_TYPE;
        boundingPoly: BoundingPolyRect;
        confidence?: number | null;
      };
      vendor?: {
        shapeType?: ShapeType;
        redactionCode?: IndividualRedactionCode;
        redactionConfig?: RedactionConfig;
        isDeleted?: boolean;
      };
    }[];
  };
};

export interface DetectionEngineV1Inbound {
  readonly engineResults: {
    readonly records: ReadonlyArray<
      DeepReadonly<
        DeepPick<
          DeepNonNullable<HeadDetectionEngineResultV1>,
          {
            tdoId: true;
            assetId: true;
            engineId: true;
            startOffsetMs: true;
            stopOffsetMs: true;
            jsondata: {
              generatedDateUTC: true;
              modifiedDateTime: true;
              sourceEngineId: true;
              taskId: true;
              series: {
                startTimeMs: true;
                stopTimeMs: true;
                object: {
                  label: true;
                  type: true;
                  boundingPoly: true;
                  confidence: true;
                };
                vendor: {
                  shapeType: true;
                  redactionCode: true;
                  redactionConfig: true;
                  isDeleted: true;
                };
              }[];
            };
          }
        >
      >
    >;
  };
}

export const detectionEngineV1Adapter = (): Adapter<
  DetectionEngineV1Inbound,
  DetectionEngineNormalizedData
> => {
  const isMatch = (i: unknown): i is DetectionEngineV1Inbound =>
    get(i, 'engineResults.records', []).every((r: any) => !('version' in r));

  const adapt: ReturnType<typeof detectionEngineV1Adapter>['adapt'] = ({
    engineResults: { records },
  }) => {
    if (!records || !arrayHasContents(records)) {
      // TODO: Why do we need to cast this? Shouldn't the returned object be valid?
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return <any>{
        jsondata: {
          version: '1.0.0',
          series: [],
        },
        isEmptyResults: true,
      };
    } else {
      return ((r) => ({
        ...r,
        jsondata: {
          ...r.jsondata,
          version: '1.0.0',
          series: r.jsondata.series.map((s) => ({
            ...s,
            // engineId: r.engineId, // don't need this!
            vendor: {
              ...s.vendor,
            },
          })),
        },
      }))(records[0]);
    }
  };

  return { isMatch, adapt };
};
